<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { useTicketsStore } from 'dashboard/stores/tickets';
import { useTicketColumnsStore } from 'dashboard/stores/ticketColumns';
import TicketDetailDialog from '../components/TicketDetailDialog.vue';

const PRIORITY_STYLE = {
  urgent: 'bg-n-ruby-3 text-n-ruby-11',
  high: 'bg-n-amber-3 text-n-amber-11',
  medium: 'bg-n-blue-3 text-n-blue-11',
  low: 'bg-n-slate-3 text-n-slate-11',
};

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const ticketsStore = useTicketsStore();
const columnsStore = useTicketColumnsStore();

const agents = useMapGetter('agents/getAgents');
const accountLabels = useMapGetter('labels/getLabels');

const ticketDetailRef = ref(null);

const filterColumn = ref('');
const filterPriority = ref('');
const filterLabel = ref('');
const viewMode = ref('active');

const isDeletedView = computed(() => viewMode.value === 'deleted');

const columns = computed(() => columnsStore.getColumns);
const tickets = computed(() => {
  let list = (isDeletedView.value
    ? ticketsStore.getDeletedList
    : ticketsStore.getTicketsList)
    .slice()
    .sort((a, b) => b.id - a.id);
  if (filterColumn.value) {
    list = list.filter(ticket => ticket.ticket_column_id === Number(filterColumn.value));
  }
  if (filterPriority.value) {
    list = list.filter(ticket => ticket.priority === filterPriority.value);
  }
  if (filterLabel.value) {
    list = list.filter(ticket => (ticket.labels || []).includes(filterLabel.value));
  }
  return list;
});
const isFetching = computed(() => ticketsStore.getUIFlags.fetchingList);

const columnFilterOptions = computed(() => [
  { value: '', label: t('TICKETS.FILTER.ALL_COLUMNS') },
  ...columns.value.map(c => ({ value: String(c.id), label: c.name })),
]);
const priorityFilterOptions = computed(() => [
  { value: '', label: t('TICKETS.FILTER.ALL_PRIORITIES') },
  ...['urgent', 'high', 'medium', 'low'].map(p => ({
    value: p,
    label: t(`TICKETS.PRIORITY.${p.toUpperCase()}`),
  })),
]);
const labelFilterOptions = computed(() => [
  { value: '', label: t('TICKETS.FILTER.ALL_LABELS') },
  ...accountLabels.value.map(l => ({ value: l.title, label: l.title })),
]);
const viewOptions = computed(() => [
  { value: 'active', label: t('TICKETS.LIST.VIEW_ACTIVE') },
  { value: 'deleted', label: t('TICKETS.LIST.VIEW_DELETED') },
]);

const columnFor = ticket =>
  columns.value.find(c => c.id === ticket.ticket_column_id);

const openEdit = ticket => ticketDetailRef.value?.open(ticket);

const onDelete = async ticket => {
  try {
    await ticketsStore.softDelete(ticket.id);
  } catch {
    useAlert(t('TICKETS.DETAIL.DELETE_ERROR'));
  }
};

const onRestore = async ticket => {
  try {
    await ticketsStore.restore(ticket.id);
  } catch {
    useAlert(t('TICKETS.DETAIL.RESTORE_ERROR'));
  }
};

watch(isDeletedView, deleted => {
  if (deleted) ticketsStore.fetchDeleted();
});

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  columnsStore.get();
  ticketsStore.get();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <header
      class="flex items-center gap-3 px-6 py-4 border-b border-n-weak"
    >
      <h1 class="text-lg font-semibold text-n-slate-12">
        {{ t('TICKETS.LIST.TITLE') }}
      </h1>
      <router-link
        :to="{ name: 'tickets_dashboard_index', params: { accountId: route.params.accountId } }"
        class="text-sm font-medium text-n-slate-11 hover:text-n-slate-12"
      >
        {{ t('TICKETS.BOARD.TITLE') }}
      </router-link>
      <div class="flex items-center gap-2 ml-auto">
        <Select v-model="viewMode" :options="viewOptions" />
        <Select v-model="filterColumn" :options="columnFilterOptions" />
        <Select v-model="filterPriority" :options="priorityFilterOptions" />
        <Select v-model="filterLabel" :options="labelFilterOptions" />
      </div>
    </header>

    <div v-if="isFetching" class="flex items-center justify-center p-10">
      <Spinner :size="22" class="text-n-slate-11" />
    </div>
    <div
      v-else-if="tickets.length === 0"
      class="flex items-center justify-center p-10"
    >
      <span class="text-sm text-n-slate-11">
        {{ isDeletedView ? t('TICKETS.LIST.DELETED_EMPTY') : t('TICKETS.LIST.EMPTY') }}
      </span>
    </div>

    <ul v-else class="flex flex-col divide-y divide-n-weak">
      <li
        v-for="ticket in tickets"
        :key="ticket.id"
        class="flex items-center gap-3 px-6 py-3 cursor-pointer group hover:bg-n-solid-2"
        @click="openEdit(ticket)"
      >
        <span
          class="w-2.5 h-2.5 rounded-full shrink-0"
          :style="{ backgroundColor: columnFor(ticket)?.color || '#94a3b8' }"
          :title="columnFor(ticket)?.name"
        />
        <span class="text-xs shrink-0 text-n-slate-10 tabular-nums w-12">
          #{{ ticket.display_id }}
        </span>
        <span class="flex-1 text-sm truncate text-n-slate-12">
          {{ ticket.title }}
        </span>
        <span
          v-if="ticket.priority"
          class="px-2 py-0.5 text-xs font-medium rounded-full shrink-0"
          :class="PRIORITY_STYLE[ticket.priority]"
        >
          {{ t(`TICKETS.PRIORITY.${ticket.priority.toUpperCase()}`) }}
        </span>
        <span class="text-xs shrink-0 text-n-slate-11 w-28 truncate text-right">
          {{ columnFor(ticket)?.name }}
        </span>
        <div class="flex items-center justify-end w-8 shrink-0">
          <Avatar
            v-if="ticket.assignee"
            :name="ticket.assignee.name"
            :src="ticket.assignee.avatar_url"
            :size="22"
            rounded-full
          />
        </div>
        <div class="w-7 shrink-0">
          <Button
            v-if="isDeletedView"
            icon="i-lucide-rotate-ccw"
            variant="ghost"
            color="slate"
            size="xs"
            class="opacity-0 group-hover:opacity-100"
            :title="t('TICKETS.DETAIL.RESTORE')"
            @click.stop="onRestore(ticket)"
          />
          <Button
            v-else
            icon="i-lucide-trash-2"
            variant="ghost"
            color="ruby"
            size="xs"
            class="opacity-0 group-hover:opacity-100"
            :title="t('TICKETS.DETAIL.DELETE')"
            @click.stop="onDelete(ticket)"
          />
        </div>
      </li>
    </ul>

    <TicketDetailDialog ref="ticketDetailRef" />
  </div>
</template>
